import { useEffect, useState } from "react";
import {
  VuiButtonPrimary,
  VuiCopyButton,
  VuiFlexContainer,
  VuiSpacer,
  VuiSpinner,
  VuiTab,
  VuiTable,
  VuiTabs,
  VuiText
} from "@vectara/vectara-ui";
import { BillingData, BillingPeriod, BillingPeriodDetail, ScheduledPayment } from "../../utils/commonTypes";
import { useCustomerContext } from "../../utils/CustomerContext";
import { useLoginContext } from "../../utils/LoginContext";
import { readBillingData } from "../../api/api";

const formatCurrency = (amount: number, currency: string): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency
  }).format(amount);
};

const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
};

export const CustomerBilling = () => {
  const { customer } = useCustomerContext();
  const { authToken } = useLoginContext();

  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeAccordions, setActiveAccordions] = useState<Record<number, boolean>>({});
  const [activeTab, setActiveTab] = useState<string>("periods");

  const fetchBillingData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await readBillingData(customer.customerId, authToken);
      setBillingData(data);
    } catch (e) {
      console.error(e);
      setError("Failed to load billing data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBillingData();
    // eslint-disable-next-line
  }, [authToken, customer.customerId]);

  // Create lookup map for billing period details by period ID
  const billingPeriodDetailsMap: Record<number, BillingPeriodDetail[]> = {};
  if (billingData?.billing_period_details) {
    billingData.billing_period_details.forEach((detail) => {
      if (!billingPeriodDetailsMap[detail.billing_period_id]) {
        billingPeriodDetailsMap[detail.billing_period_id] = [];
      }
      billingPeriodDetailsMap[detail.billing_period_id].push(detail);
    });
  }

  const toggleAccordion = (periodId: number) => {
    setActiveAccordions({
      ...activeAccordions,
      [periodId]: !activeAccordions[periodId]
    });
  };

  // Define table columns for billing periods
  const billingPeriodColumns = [
    {
      name: "period",
      header: {
        render: () => "Billing Period"
      },
      render: (period: BillingPeriod) => {
        return (
          <>
            {formatTimestamp(period.period_start)} - {formatTimestamp(period.period_end)}
          </>
        );
      }
    },
    {
      name: "amount",
      header: {
        render: () => "Amount Due"
      },
      render: (period: BillingPeriod) => {
        return <>{formatCurrency(period.amount_due, period.currency)}</>;
      }
    },
    {
      name: "status",
      header: {
        render: () => "Status"
      },
      render: (period: BillingPeriod) => {
        return (
          <span className={`font-semibold ${period.status === "PAID" ? "text-green-600" : "text-red-600"}`}>
            {period.status}
          </span>
        );
      }
    },
    {
      name: "actions",
      header: {
        render: () => "Details"
      },
      render: (period: BillingPeriod) => {
        return (
          <VuiButtonPrimary color="primary" size="s" onClick={() => toggleAccordion(period.id)}>
            {activeAccordions[period.id] ? "Hide Details" : "Show Details"}
          </VuiButtonPrimary>
        );
      }
    }
  ];

  // Define table columns for billing period details
  const billingPeriodDetailColumns = [
    {
      name: "description",
      header: {
        render: () => "Description"
      },
      render: (detail: BillingPeriodDetail) => {
        return <>{detail.item_description}</>;
      }
    },
    {
      name: "pricing_plan",
      header: {
        render: () => "Plan"
      },
      render: (detail: BillingPeriodDetail) => {
        // Show plan ID and type if available
        if (detail.customer_plan_id) {
          return (
            <div>
              <VuiFlexContainer alignItems="center" spacing="xs">
                <div>{detail.customer_plan_id}</div>
                <VuiCopyButton size="xs" value={detail.customer_plan_id} />
              </VuiFlexContainer>
              {detail.plan_type && <div className="text-xs text-gray-600">Type: {detail.plan_type}</div>}
            </div>
          );
        }
        // Fallback to agreement ID
        return (
          <VuiFlexContainer alignItems="center" spacing="xs">
            <div>{`Agmt: ${detail.pricing_agreement_id}`}</div>
            <VuiCopyButton size="xs" value={detail.pricing_agreement_id.toString()} />
          </VuiFlexContainer>
        );
      }
    },
    {
      name: "quantity",
      header: {
        render: () => "Quantity"
      },
      render: (detail: BillingPeriodDetail) => {
        return <>{detail.quantity.toLocaleString()}</>;
      }
    },
    {
      name: "unitPrice",
      header: {
        render: () => "Unit Price"
      },
      render: (detail: BillingPeriodDetail) => {
        return <>{formatCurrency(detail.unit_price, detail.currency)}</>;
      }
    },
    {
      name: "total",
      header: {
        render: () => "Total"
      },
      render: (detail: BillingPeriodDetail) => {
        return <>{formatCurrency(detail.amount, detail.currency)}</>;
      }
    },
    {
      name: "comment",
      header: {
        render: () => "Comment"
      },
      render: (detail: BillingPeriodDetail) => {
        return <>{detail.comment || "-"}</>;
      }
    }
  ];


  const renderBillingPeriodTable = () => {
    if (!billingData?.billing_periods.length) {
      return <VuiText>No billing periods found.</VuiText>;
    }

    return (
      <div className="bg-white shadow-sm rounded-lg">
        {/* Header row - only display once */}
        <div className="grid grid-cols-4 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 font-semibold">
          <div>Billing Period</div>
          <div>Amount Due</div>
          <div>Status</div>
          <div>Details</div>
        </div>

        {/* Billing period rows */}
        {billingData.billing_periods.map((period) => (
          <div key={period.id}>
            <div className="grid grid-cols-4 gap-4 px-4 py-3 border-b border-gray-200 hover:bg-gray-50">
              <div>
                {formatTimestamp(period.period_start)} - {formatTimestamp(period.period_end)}
              </div>
              <div>{formatCurrency(period.amount_due, period.currency)}</div>
              <div>
                <span
                  className={`font-semibold ${
                    period.amount_due === 0
                      ? "text-gray-400"
                      : period.status === "PAID"
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {period.amount_due === 0 ? "N/A" : period.status}
                </span>
              </div>
              <div>
                <VuiButtonPrimary color="primary" size="s" onClick={() => toggleAccordion(period.id)}>
                  {activeAccordions[period.id] ? "Hide Details" : "Show Details"}
                </VuiButtonPrimary>
              </div>
            </div>

            {activeAccordions[period.id] && (
              <div className="p-4 bg-gray-50 border-b border-gray-200 ml-8 mr-8 w-3/4">
                <VuiFlexContainer justifyContent="spaceBetween" alignItems="center">
                  <VuiText size="s">
                    <h4>Invoice Details</h4>
                  </VuiText>

                  {period.stripe_invoice_id && (
                    <VuiFlexContainer alignItems="center" spacing="xs">
                      <div className="text-sm text-gray-600">Invoice ID: {period.stripe_invoice_id}</div>
                      <VuiCopyButton size="xs" value={period.stripe_invoice_id} />
                    </VuiFlexContainer>
                  )}
                </VuiFlexContainer>

                <VuiSpacer size="s" />

                {billingPeriodDetailsMap[period.id]?.length ? (
                  <VuiTable
                    fluid
                    idField={(detail: BillingPeriodDetail) => detail.id.toString()}
                    columns={billingPeriodDetailColumns}
                    rows={billingPeriodDetailsMap[period.id] || []}
                    className="w-full" // Ensure table takes full width
                  />
                ) : (
                  <VuiText>No details available for this billing period.</VuiText>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderScheduledPaymentsTable = () => {
    if (!billingData?.scheduled_payments.length) {
      return <VuiText>No scheduled payments found.</VuiText>;
    }

    return (
      <div className="bg-white shadow-sm rounded-lg">
        {/* Header row */}
        <div className="grid grid-cols-6 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 font-semibold">
          <div>Scheduled Payment Date</div>
          <div>Payment Processed Date</div>
          <div>Amount</div>
          <div>Status</div>
          <div>Attempt</div>
          <div>Retry</div>
        </div>

        {/* Scheduled payment rows */}
        {billingData.scheduled_payments.map((payment) => (
          <div key={payment.id} className="grid grid-cols-6 gap-4 px-4 py-3 border-b border-gray-200 hover:bg-gray-50">
            <div>{formatTimestamp(payment.scheduled_date)}</div>
            <div>{payment.processed_date ? formatTimestamp(payment.processed_date) : "-"}</div>
            <div>{formatCurrency(payment.amount, payment.currency)}</div>
            <div>
              {(() => {
                let displayStatus = payment.status;
                let statusColor = "text-gray-600";

                if (payment.status === "completed") {
                  displayStatus = "PAID";
                  statusColor = "text-green-600";
                } else if (payment.status === "unstarted" || payment.status === "started") {
                  displayStatus = "PENDING";
                  statusColor = "text-yellow-600";
                } else if (payment.status === "failed" || payment.status === "aborted") {
                  displayStatus = "FAILED";
                  statusColor = "text-red-600";
                }

                return <span className={`font-semibold ${statusColor}`}>{displayStatus}</span>;
              })()}
            </div>
            <div>{payment.attempt_ctr || 0}</div>
            <div>{payment.retry_ctr || 0}</div>
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <VuiFlexContainer direction="column" alignItems="center">
        <VuiText>
          <i>Loading billing data, please wait...</i>
        </VuiText>
        <VuiSpacer size="l" />
        <VuiSpinner size="m" />
      </VuiFlexContainer>
    );
  }

  if (error) {
    return (
      <VuiFlexContainer direction="column">
        <VuiText>
          <p>{error}</p>
        </VuiText>
        <VuiSpacer size="m" />
        <VuiButtonPrimary color="primary" onClick={fetchBillingData}>
          Retry
        </VuiButtonPrimary>
      </VuiFlexContainer>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "periods":
        return renderBillingPeriodTable();
      case "payments":
        return renderScheduledPaymentsTable();
      default:
        return renderBillingPeriodTable();
    }
  };

  return (
    <VuiFlexContainer direction="column">
      <VuiTabs size="m">
        <VuiTab isActive={activeTab === "periods"} onClick={() => setActiveTab("periods")}>
          Billing Periods
        </VuiTab>
        <VuiTab isActive={activeTab === "payments"} onClick={() => setActiveTab("payments")}>
          Scheduled Payments
        </VuiTab>
      </VuiTabs>

      <VuiSpacer size="m" />

      <div className="w-[90%] mx-auto">{renderTabContent()}</div>
    </VuiFlexContainer>
  );
};
